import { Dispatch, SetStateAction } from 'react';
import { HttpResponse } from '../../../../../common/ajax';
import { inputChecks } from '../../../../../common/global/helpers';
import { BudgetTypes, CustomClientTypes, ProductTypes } from '../../../enums';
import { wait } from '../../../helpers';
import { Dictionary } from '../../../models/Categories';
import { CheckoutVoucherResponseModel } from '../../../models/Checkout';
import { ErrorState } from '../../../models/Errors';
import {
  DependentsConfigModel,
  ProductOption,
  ProductRecurrence,
} from '../../../models/Options';
import { PaymentMethodModel } from '../../../models/PaymentMethods';
import { TaxInformation } from '../../../models/Product';
import { ProductPaymentMethodDetailsModel } from '../../../models/ProductPaymentMethodDetailsModel';
import { resources } from '../../../resources/resources';
import { cartService } from '../../../services/cart';
import { contactService } from '../../../services/contact';
import { productsService } from '../../../services/products';
import { voucherService } from '../../../services/vouchers';
import { processForm } from '../../Common/Options';
import { Budget } from '../../../contexts/common';

export interface ProductDetailsFormProps {
  productId: number;
  productName?: string;
  productTypeId: number;
  defaultPrice: number;
  discountedPrice?: number;
  priceRanges?: number[];
  priceOptionId?: number;
  voucherDiscount?: string;
  voucherCode?: string;
  url?: string;
  taxRate?: number;
  isFavorite: boolean;
  options: ProductOption[];
  customClientType: CustomClientTypes;
  dependentsConfig?: DependentsConfigModel;
  recurrences: ProductRecurrence[];
  isRecurring: boolean;
  hidePriceAndTaxes: boolean;
  softLimitMessage?: string;
  taxInformation?: TaxInformation;
  isPayableByCard: boolean;
  isPayableByCashback: boolean;
  showNoCodes: boolean;
  showTemporaryDeactivate: boolean;
  customLabel?: string;
  canElectNow: boolean;
  errors: ErrorState;
  paymentMethodDetails?: ProductPaymentMethodDetailsModel[];
  paymentMethods?: PaymentMethodModel[];
  customerBudget: Budget;
  updateCustomerBudgetCashback: (value: number) => void;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
  setPaymentError: (value: string | undefined) => void;
}

export const getActionButtonLabel = (productTypeId: number): string => {
  switch (productTypeId) {
    case ProductTypes.CashBack:
      return resources.products.actions.goToStore;
    case ProductTypes.ContactVoucher:
      return resources.products.actions.contactMe;
    case ProductTypes.FreeLinkVoucher:
      return resources.products.actions.goToWebsite;
    case ProductTypes.FreePdfVoucher:
      return resources.products.actions.downloadVoucher;
    default:
      return resources.products.actions.addToCard;
  }
};

//function execute a fire & forget async operation with a callback
export async function getProductFormAction({
  productId,
  productTypeId,
  form,
  options,
  cashbackDiscount,
  link,
  selectedCheckoutMethod,
  isSalaryDeductionSelected,
  handlers: {
    handleAddToCartResponse,
    handleContactResponse,
    handleVoucherCheckoutResponse,
    handleRedirectResponse,
    handleCashbackResponse,
  },
}: {
  productId: number;
  productTypeId: number;
  form: Dictionary<any>;
  cashbackDiscount: number;
  options: ProductOption[];
  link?: string;
  selectedCheckoutMethod?: BudgetTypes;
  isSalaryDeductionSelected: boolean;
  handlers: {
    handleAddToCartResponse: (r: HttpResponse<any>) => void;
    handleContactResponse: (r: HttpResponse<any>) => void;
    handleVoucherCheckoutResponse: (
      r: HttpResponse<CheckoutVoucherResponseModel>,
    ) => Promise<void>;
    handleRedirectResponse: () => void;
    handleCashbackResponse: () => void;
  };
}): Promise<void> {
  switch (productTypeId) {
    case ProductTypes.DefaultProduct:
      await handleAddToCart(
        productId,
        form,
        options,
        cashbackDiscount,
        handleAddToCartResponse,
        selectedCheckoutMethod,
        isSalaryDeductionSelected,
      );
      break;
    case ProductTypes.ContactVoucher:
      await handleContactMe(productId, form, options, handleContactResponse);
      break;
    case ProductTypes.FreePdfVoucher:
      await handleDownloadVoucher(
        productId,
        form,
        options,
        handleVoucherCheckoutResponse,
      );
      break;
    case ProductTypes.FreeLinkVoucher:
      handleNavigateToSite(link ?? '', handleRedirectResponse);
      break;
    case ProductTypes.CashBack:
      await handleCashBackRedirect(productId, handleCashbackResponse);
      break;
  }
}

const handleNavigateToSite = (
  siteUrl: string,
  handleRedirectResponse: () => void,
) => {
  window.open(siteUrl);
  handleRedirectResponse();
};

const handleDownloadVoucher = async (
  productId: number,
  form: Dictionary<any>,
  options: ProductOption[],
  handleVoucherCheckoutResponse: (
    response: HttpResponse<CheckoutVoucherResponseModel>,
  ) => Promise<void>,
) => {
  if (validateForm(form, options)) {
    const result = await voucherService.checkoutVoucherOrder(
      productId,
      processForm(form, options),
    );
    await wait(500);
    handleVoucherCheckoutResponse(result);
  }
};

const handleContactMe = async (
  productId: number,
  form: Dictionary<any>,
  options: ProductOption[],
  handleContactResponse: (response: HttpResponse<any>) => void,
) => {
  if (validateForm(form, options)) {
    const result = await contactService.contact(
      productId,
      processForm(form, options),
    );
    handleContactResponse(result);
  }
};

const handleAddToCart = async (
  productId: number,
  form: Dictionary<any>,
  options: ProductOption[],
  cashbackDiscount: number,
  handleAddToCartResponse: (response: HttpResponse<any>) => void,
  selectedCheckoutMethod?: BudgetTypes,
  isSalaryDeductionSelected?: boolean,
) => {
  if (validateForm(form, options)) {
    const result = await cartService.addProduct(
      productId,
      1,
      cashbackDiscount,
      processForm(form, options),
      selectedCheckoutMethod,
      isSalaryDeductionSelected,
    );
    handleAddToCartResponse(result);
  }
};

const handleCashBackRedirect = async (
  productId: number,
  handleCashbackResponse: () => void,
) => {
  const result = await productsService.getCashBackLink(productId);
  handleCashbackResponse();
  if (!result.hasErrors) {
    window.open(result.data);
  }
};

const validateForm = (form: Dictionary<any>, options: ProductOption[]) => {
  let validationResult = true;
  for (const option of options) {
    let check: any = () => true;
    if (option.validations.number) {
      check = inputChecks.isNumber;
    } else if (option.validations.decimal) {
      check = inputChecks.isDecimal;
    } else if (option.validations.email) {
      check = inputChecks.isEmail;
    }

    validationResult = validationResult && check(form[option.id]);

    if (option.required && !form[option.id]) {
      validationResult = false;
    }
  }

  return validationResult || true;
};
